import { useRef } from "react";
import { motion, useInView } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];

// Reliable latching in-view hook.
function useReveal(amount = 0.2) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "0px 0px -12% 0px", amount });
  return [ref, inView];
}

// Simple in-view fade/rise reveal.
export const Reveal = ({ children, delay = 0, y = 28, className = "", ...rest }) => {
  const [ref, inView] = useReveal(0.15);
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y }}
      transition={{ duration: 0.9, ease: EASE, delay }}
      className={className}
      {...rest}
    >
      {children}
    </motion.div>
  );
};

// Line-by-line masked reveal. `lines` is an array of strings/nodes.
export const MaskText = ({ lines, className = "", delay = 0, stagger = 0.09, onLoad = false }) => {
  const [ref, inView] = useReveal(0.1);
  const show = onLoad || inView;
  return (
    <span ref={ref} className={className}>
      {lines.map((line, i) => (
        <span key={i} className="mask-line">
          <motion.span
            className="block"
            initial={{ y: "110%" }}
            animate={{ y: show ? "0%" : "110%" }}
            transition={{ duration: 1, ease: EASE, delay: delay + i * stagger }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </span>
  );
};

// Staggered container for lists.
export const Stagger = ({ children, className = "", amount = 0.12, ...rest }) => {
  const [ref, inView] = useReveal(0.12);
  return (
    <motion.div
      ref={ref}
      className={className}
      initial="hidden"
      animate={inView ? "show" : "hidden"}
      variants={{ show: { transition: { staggerChildren: amount } } }}
      {...rest}
    >
      {children}
    </motion.div>
  );
};

export const StaggerItem = ({ children, className = "", y = 24 }) => (
  <motion.div
    className={className}
    variants={{
      hidden: { opacity: 0, y },
      show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: EASE } },
    }}
  >
    {children}
  </motion.div>
);
